/**
 * Payment Page - Simplified Razorpay Checkout Integration
 * 2025 Pricing: Individual courses OR All Access only
 * Shows INR for India, USD for international (payment always in INR via Razorpay)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { COURSE_DATA, ALL_ACCESS, PRICING } from '../constants/courseData';
import { trackCheckoutStart, trackPurchase } from '../utils/metaPixel';
import { 
  CreditCard, Shield, CheckCircle, AlertCircle, 
  BookOpen, Clock, Award, ArrowLeft, Loader2, Crown, Sparkles, Globe, Info
} from 'lucide-react';

const Payment = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isIndia, currency, loading: currencyLoading } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [courseInfo, setCourseInfo] = useState(null);
  const [allAccessPricing, setAllAccessPricing] = useState(null);

  // Fetch All Access pricing for existing customers
  useEffect(() => {
    const fetchAllAccessPricing = async () => {
      if (courseId === 'ALL_ACCESS') {
        try {
          const pricing = await apiService.getAllAccessPricing();
          setAllAccessPricing(pricing);
        } catch (err) {
          console.error('Failed to fetch All Access pricing:', err);
        }
      }
    };
    fetchAllAccessPricing();
  }, [courseId]);

  // Build course info from data
  useEffect(() => {
    // Handle All Access purchase
    if (courseId === 'ALL_ACCESS') {
      const finalPriceINR = allAccessPricing?.final_price || ALL_ACCESS.price;
      const creditINR = allAccessPricing?.credit || 0;
      
      // Build dynamic course category lists from COURSE_DATA
      const allCodes = Object.keys(COURSE_DATA);
      const linuxCodes = allCodes.filter(c => ['RH124', 'RH134', 'SVRADM'].includes(c));
      const devopsCodes = allCodes.filter(c => ['ANSIBLE', 'DOCKER', 'TERRAFORM', 'KUBERNETES', 'GIT'].includes(c));
      const progCodes = allCodes.filter(c => ['PYTHON', 'C_LANG', 'CPP', 'JAVA'].includes(c));
      const dbCodes = allCodes.filter(c => ['MYSQL'].includes(c));
      const examCodes = allCodes.filter(c => c.startsWith('EXAM_'));
      
      const progNames = progCodes.map(c => c === 'C_LANG' ? 'C' : c === 'CPP' ? 'C++' : c === 'JAVA' ? 'Core Java' : c);
      const dbNames = dbCodes.map(c => c === 'MYSQL' ? 'MySQL/MariaDB' : c);

      setCourseInfo({
        title: ALL_ACCESS.name,
        description: ALL_ACCESS.description,
        priceINR: finalPriceINR,
        priceUSD: ALL_ACCESS.price_usd,
        basePriceINR: ALL_ACCESS.price,
        creditINR: creditINR,
        duration: `${ALL_ACCESS.labs} hands-on labs across ${ALL_ACCESS.courses.length} courses`,
        features: [
          '👑 Lifetime access to 13 technology courses',
          `📚 ${ALL_ACCESS.courses.length} courses included`,
          `🔬 ${ALL_ACCESS.labs}+ hands-on labs`,
          `💻 Programming (${progNames.join(', ')})`,
          ...(dbNames.length > 0 ? [`🗄️ Database (${dbNames.join(', ')})`] : []),
          `🐳 DevOps (${devopsCodes.join(', ')})`,
          `🐧 Linux (${linuxCodes.join(', ')})`,
          '📜 Course completion certificates',
          '🔄 Future course updates included',
          '💰 Best value - Save thousands!'
        ],
        notIncluded: isIndia ? [
          '❌ Mock Tests (₹499)',
          '❌ Real-World Projects Hub (₹99 per project)'
        ] : [
          '❌ Mock Tests ($19)',
          '❌ Real-World Projects Hub ($2 per project)'
        ],
        isAllAccess: true
      });
      return;
    }

    // Handle Projects Access purchase (per-project)
    if (courseId.startsWith('PROJECT_')) {
      setCourseInfo({
        title: 'Project Access',
        description: 'Get access to this project with detailed step-by-step guide. Practice on your own VM/AWS environment.',
        priceINR: 99,
        priceUSD: 2,
        duration: 'Lifetime Access',
        features: [
          'Complete step-by-step project guide',
          'Structured sections with clear instructions',
          'Mark as Done progress tracking',
          'Lifetime access to this project',
        ]
      });
      return;
    }

    // Handle Mock Tests bundle - Dynamic from catalog API
    if (courseId === 'MOCK_TESTS') {
      // Fetch catalog to get dynamic counts
      const buildMockTestInfo = async () => {
        let totalTests = 43;
        let categoryNames = [];
        try {
          const response = await apiService.getTestCatalog();
          if (response?.catalog) {
            totalTests = Object.values(response.catalog).reduce((sum, tests) => sum + tests.length, 0);
            categoryNames = response.categories || Object.keys(response.catalog);
          }
        } catch (e) {
          // Fallback to static if API fails
        }

        // Group categories for features
        const linuxCats = categoryNames.filter(c => ['RH124', 'RH134', 'SVRADM'].includes(c));
        const devopsCats = categoryNames.filter(c => ['ANSIBLE', 'DOCKER', 'TERRAFORM', 'KUBERNETES', 'GIT'].includes(c));
        const progCats = categoryNames.filter(c => ['PYTHON', 'C_LANG', 'CPP', 'JAVA'].includes(c));
        const dbCats = categoryNames.filter(c => ['MYSQL'].includes(c));

        const features = [];
        if (linuxCats.length > 0) features.push(`${linuxCats.join(', ')} Linux Mock Tests`);
        if (devopsCats.length > 0) features.push(`${devopsCats.join(', ')} DevOps Tests`);
        if (progCats.length > 0) {
          const names = progCats.map(c => c === 'C_LANG' ? 'C' : c === 'CPP' ? 'C++' : c === 'JAVA' ? 'Core Java' : c);
          features.push(`${names.join(', ')} Programming Tests`);
        }
        if (dbCats.length > 0) features.push('MySQL/MariaDB Database Tests');
        features.push('Interview-Style Questions', 'Instant Score & Detailed Explanations', 'Progress Tracking & Analytics', 'Unlimited Retakes');

        setCourseInfo({
          title: 'Interview Prep Mock Tests',
          description: `Crack your tech interviews! ${totalTests}+ mock tests covering ${categoryNames.length} courses.`,
          priceINR: 499,
          priceUSD: 19,
          duration: `${totalTests}+ Mock Tests • Unlimited Attempts`,
          features
        });
      };
      buildMockTestInfo();
      return;
    }

    // Handle individual course purchase
    const course = COURSE_DATA[courseId];
    if (course) {
      setCourseInfo({
        title: course.fullName,
        description: course.description,
        priceINR: course.price,
        priceUSD: course.priceUSD || 19,
        duration: `${course.labs} hands-on labs${course.freeLabs > 0 ? ` (${course.freeLabs} free)` : ''}`,
        features: getCourseFeatures(courseId),
        category: course.category
      });
      return;
    }

    // Invalid course
    setError('Invalid course selected');
  }, [courseId, allAccessPricing, isIndia]);

  // Meta Pixel: InitiateCheckout — fires once courseInfo is ready (i.e. user
  // is actually on a valid payment page with a real price). Depending on
  // courseInfo.id + priceINR/priceUSD (primitives) avoids re-fire on every
  // render when only object reference changes.
  // Currency matches what the user sees: INR for India, USD for international.
  useEffect(() => {
    const price = isIndia ? courseInfo?.priceINR : courseInfo?.priceUSD;
    if (courseInfo && price > 0) {
      trackCheckoutStart({
        courseId: courseInfo.id || courseId,
        amount: price,
        currency: isIndia ? 'INR' : 'USD',
      });
    }
  }, [courseInfo, courseId, isIndia]);

  // Get course-specific features
  const getCourseFeatures = (id) => {
    const featureMap = {
      'RH124': [
        `All ${COURSE_DATA.RH124.labs} Linux Foundation Labs`,
        'System Administration Mastery', 
        'File Management & Permissions',
        'Bash Scripting & Automation',
        'RHCSA Certification Prep (Part 1)',
        'Certificate of Completion'
      ],
      'RH134': [
        'Advanced Linux Administration',
        'System Performance & Monitoring', 
        'Security & User Management',
        'Network Services & Configuration',
        'Storage & File System Management',
        'RHCSA Certification Ready (Part 2)'
      ],
      'SVRADM': [
        'SSH Server Configuration & Security',
        'DNS Server (BIND) Setup',
        'Apache & Nginx Web Servers',
        'NFS & Samba File Sharing',
        'MariaDB Database Administration',
        'Firewall Configuration (firewalld)'
      ],
      'ANSIBLE': [
        'Ansible Installation & Configuration',
        'Inventory Management & Host Patterns',
        'Playbook Development & Best Practices',
        'Handlers & Error Handling',
        'Jinja2 Templates & Roles',
        'RHCE Certification Prep'
      ],
      'DOCKER': [
        'Docker Installation & Configuration',
        'Container Lifecycle Management',
        'Docker Images & Registries',
        'Volumes & Persistent Storage',
        'Docker Networking & Compose',
        'Real-world Projects'
      ],
      'TERRAFORM': [
        'Terraform Installation & Setup',
        'HCL Syntax & Providers',
        'Variables & State Management',
        'AWS EC2, VPC & S3',
        'IAM & Security Groups',
        'Complete 3-Tier Architecture Project'
      ],
      'KUBERNETES': [
        'k3s Installation & kubectl Setup',
        'Pods, Deployments & ReplicaSets',
        'Services, Ingress & DNS',
        'ConfigMaps, Secrets & PV/PVC',
        'Helm, RBAC & Troubleshooting',
        'Full Stack Mini Project'
      ],
      'GIT': [
        'Git Installation & Configuration',
        'Branching & Merging',
        'Resolving Merge Conflicts',
        'Git Rebase & Cherry-pick',
        'GitHub Pull Requests',
        'Real-world Collaboration Workflow'
      ],
      'PYTHON': [
        'Python Basics & Syntax',
        'Functions & Modules',
        'Object Oriented Programming',
        'File Handling & I/O',
        'Data Structures',
        'Real-world Projects'
      ],
      'C_LANG': [
        'C Basics & Data Types',
        'Pointers & Memory Management',
        'Arrays & Strings',
        'Structures & Unions',
        'File Handling',
        'Real-world Projects'
      ],
      'CPP': [
        'C++ Basics & OOP Concepts',
        'Classes & Objects',
        'Inheritance & Polymorphism',
        'STL Containers & Algorithms',
        'Smart Pointers & Modern C++',
        'Real-world Projects'
      ],
      'EXAM_RHCSA': [
        '3 Complete RHCSA Practice Exams',
        '45 Real Exam-Style Questions',
        'Dual VM Architecture',
        'Auto Verification for Instant Feedback',
        'Unlimited Practice',
        'Real RHCSA Exam Objectives Covered'
      ],
      'EXAM_DO188': [
        '3 Complete DO188 Practice Exams',
        '45 Container Exam Questions',
        'Podman, Buildah & Skopeo Labs',
        'Systemd Container Integration',
        'Multi-stage Builds',
        'Pod Management & Networking'
      ],
      'EXAM_EX294': [
        '3 Complete EX294 Practice Exams',
        '18 Ansible Automation Tasks',
        'Control Node + 5 Managed Nodes',
        'Playbooks, Roles & Templates',
        'Ansible Vault & Security',
        'RHEL System Roles'
      ]
    };
    return featureMap[id] || ['Expert-designed curriculum', 'Hands-on labs', 'Certificate of completion'];
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order with backend - pass is_international flag
      const orderResponse = await apiService.createOrder({
        course_id: courseId,
        is_international: !isIndia
      });

      const options = {
        key: orderResponse.key_id,
        amount: orderResponse.amount,
        currency: orderResponse.currency,
        name: 'Grras Solutions',
        description: `${courseInfo.title} - DevOps Training`,
        order_id: orderResponse.order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await apiService.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              course_id: courseId
            });

            if (verifyResponse.success) {
              // Meta Pixel: Purchase — fires ONLY after backend confirms the
              // Razorpay signature is valid. This is the most valuable signal
              // we send to Meta — every Purchase event with a real value lets
              // Meta train its ad-optimisation model toward paying users.
              // Use the price/currency the user actually saw — INR for Indian
              // users, USD for international — so Meta's ROAS reporting reflects
              // real revenue in the correct currency.
              trackPurchase({
                amount: isIndia ? (courseInfo?.priceINR || 0) : (courseInfo?.priceUSD || 0),
                currency: isIndia ? 'INR' : 'USD',
                courseId: courseInfo?.id || courseId,
                paymentId: verifyResponse.payment_id || response.razorpay_payment_id,
              });

              if (typeof window.gtag === 'function') {
                window.gtag('event', 'conversion', {
                  'send_to': 'AW-1002496102/sk_PCMfLt7scEObAg94D',
                  'value': isIndia ? courseInfo.priceINR : courseInfo.priceUSD,
                  'currency': isIndia ? 'INR' : 'USD',
                  'transaction_id': ''
                });
              }

              navigate('/payment-success', {
                state: { 
                  paymentId: verifyResponse.payment_id,
                  courseId: courseId,
                  courseName: courseInfo.title,
                  isAllAccess: courseId === 'ALL_ACCESS'
                } 
              });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            setError('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: {
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          contact: user.mobile_number || ''
        },
        theme: {
          color: '#1e40af'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment initiation error:', error);
      setError(error.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  // USD to INR conversion rate (same as backend)
  const USD_TO_INR = 90;

  // Format price based on location
  const formatDisplayPrice = () => {
    if (!courseInfo) return '';
    if (isIndia) {
      return `₹${courseInfo.priceINR?.toLocaleString('en-IN')}`;
    }
    return `$${courseInfo.priceUSD}`;
  };

  // Get converted INR amount for international users
  const getConvertedINR = () => {
    if (!courseInfo || isIndia) return null;
    return courseInfo.priceUSD * USD_TO_INR;
  };

  if (!courseInfo || currencyLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          {error ? (
            <>
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Course Not Found</h2>
              <p className="text-slate-600 mb-4">{error}</p>
            </>
          ) : (
            <Loader2 className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />
          )}
          <Button onClick={() => navigate('/pricing')} data-testid="back-to-pricing">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Pricing
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`Enroll in ${courseInfo.title} | DevOps Training`}
        description={`Secure payment for ${courseInfo.title}. Start your DevOps journey with hands-on labs.`}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
            <Button 
              variant="outline" 
              onClick={() => navigate('/pricing')}
              className="min-h-[44px] touch-manipulation w-full sm:w-auto"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Pricing
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">
                Complete Your Enrollment
              </h1>
              <p className="text-slate-600 mt-1">
                Secure payment powered by Razorpay
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Course Details */}
            <Card className="bg-white shadow-lg">
              <CardHeader className={`text-white rounded-t-lg ${
                courseInfo.isAllAccess 
                  ? 'bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500' 
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600'
              }`}>
                <div className="flex items-center gap-3">
                  {courseInfo.isAllAccess ? (
                    <Crown className="h-6 w-6" />
                  ) : (
                    <BookOpen className="h-6 w-6" />
                  )}
                  <div>
                    <CardTitle className="text-xl">{courseInfo.title}</CardTitle>
                    <Badge className="bg-white/20 text-white mt-2">
                      {courseInfo.isAllAccess ? '👑 All Access' : courseInfo.category?.toUpperCase() || 'Course'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                <p className="text-slate-600 mb-6">{courseInfo.description}</p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <span>{courseInfo.duration}</span>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-slate-800 mb-3">What you get:</h4>
                    <ul className="space-y-2">
                      {courseInfo.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {courseInfo.notIncluded && courseInfo.notIncluded.length > 0 && (
                    <div className="border-t pt-4" data-testid="all-access-not-included">
                      <h4 className="font-semibold text-rose-700 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Not included (buy separately):
                      </h4>
                      <ul className="space-y-1.5">
                        {courseInfo.notIncluded.map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-sm text-slate-600 leading-snug">{item}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[11px] text-slate-500 mt-2 italic">
                        These items are priced separately and can be purchased anytime from your dashboard.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment Card */}
            <Card className="bg-white shadow-2xl border-0 overflow-hidden">
              <CardHeader className={`text-white ${
                courseInfo.isAllAccess 
                  ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500' 
                  : 'bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-600'
              }`}>
                <CardTitle className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-xl font-bold">Payment Details</div>
                    {courseInfo.isAllAccess && (
                      <Badge className="bg-white text-amber-600 mt-2">
                        <Sparkles className="h-3 w-3 mr-1" />
                        BEST VALUE
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6 space-y-6">
                
                {/* Currency Indicator */}
                <div className="flex items-center justify-center gap-2 bg-slate-50 rounded-lg p-2">
                  <Globe className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-600">
                    Showing price in <span className="font-semibold">{isIndia ? 'INR (₹)' : 'USD ($)'}</span>
                  </span>
                </div>

                {/* Pricing */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <div className="space-y-3">
                    {/* Show credit for All Access if applicable */}
                    {courseInfo.isAllAccess && courseInfo.creditINR > 0 && isIndia && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Base Price</span>
                          <span className="text-gray-500 text-lg">₹{courseInfo.basePriceINR?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center text-emerald-600">
                          <span className="font-medium">Your Credit (existing courses)</span>
                          <span className="font-bold">- ₹{courseInfo.creditINR?.toLocaleString()}</span>
                        </div>
                        <div className="border-t pt-2"></div>
                      </>
                    )}
                    
                    <div className="flex justify-between items-center py-2">
                      <span className="font-semibold text-lg">
                        {courseInfo.isAllAccess && courseInfo.creditINR > 0 && isIndia ? 'Your Price' : 'Course Price'}
                      </span>
                      <div className="text-right">
                        <span className="text-3xl font-bold text-emerald-600">
                          {formatDisplayPrice()}
                        </span>
                        {/* Clarify USD billing for international users */}
                        {!isIndia && (
                          <div className="text-xs text-gray-500 mt-1">
                            Billed in USD via Razorpay
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {courseInfo.isAllAccess && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <span className="text-amber-800 font-medium">Total Value</span>
                          <span className="text-amber-700 font-bold">
                            {isIndia 
                              ? `₹${getTotalCoursesValue().toLocaleString()}+`
                              : `$${getTotalCoursesValueUSD()}+`
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* International Payment Note */}
                {!isIndia && (
                  <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800">International Payment</p>
                      <p className="text-blue-700">
                        You will be charged <strong>{formatDisplayPrice()} USD</strong> via Razorpay.
                        Your card will be billed in USD at the current forex rate.
                      </p>
                    </div>
                  </div>
                )}

                {/* Student Info */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
                  <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    Student Information
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-semibold text-gray-800">{user?.first_name} {user?.last_name}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-semibold text-gray-800">{user?.email}</span>
                    </div>
                  </div>
                </div>

                {/* Security */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-bold text-blue-900 mb-1">Secure Payment</p>
                      <p className="text-blue-700 text-sm">
                        Your payment is secured by Razorpay with 256-bit SSL encryption
                      </p>
                    </div>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-800">Payment Issue</p>
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </div>
                )}

                {/* Pay Button */}
                <Button 
                  onClick={handlePayment}
                  disabled={loading}
                  data-testid="pay-button"
                  className={`w-full font-bold py-6 text-lg min-h-[64px] rounded-xl shadow-lg ${
                    courseInfo.isAllAccess 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' 
                      : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                  } text-white`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-3 h-5 w-5" />
                      {isIndia 
                        ? `Pay ₹${courseInfo.priceINR?.toLocaleString('en-IN')} & Enroll Now`
                        : `Pay $${courseInfo.priceUSD} & Enroll Now`
                      }
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  By proceeding, you agree to our Terms of Service and Privacy Policy.
                  <br />
                  <span className="font-medium">30-day money-back guarantee • Instant access</span>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

// Helper functions
const getTotalCoursesValue = () => {
  return Object.values(COURSE_DATA).reduce((sum, course) => sum + course.price, 0);
};

const getTotalCoursesValueUSD = () => {
  return Object.values(COURSE_DATA).reduce((sum, course) => sum + (course.priceUSD || 19), 0);
};

export default Payment;
